/**
 * Token coverage logic for `llm-agent` nodes — kept here so
 * `LlmAgentConfig.tsx` stays focused on rendering.
 *
 * Given the agent's `{{tokens}}`, the node's `inputMapping`, the
 * workflow's trigger `inputSchema`, and the effective output fields of
 * upstream nodes, classify each token as one of:
 *
 *   covered  — will get a real value at runtime
 *   broken   — appears in inputMapping but its JSONata RHS dangles
 *   upstream — available on an upstream node, but not wired into inputs
 *   missing  — nothing provides this value
 *   unknown  — can't statically verify (complex JSONata)
 *
 * When inputMapping is empty, the runner passes the workflow trigger
 * input through verbatim — tokens matching the workflow inputSchema
 * properties are covered (green).
 */

/** Active source of the per-key resolution map. */
export type CoverageSource = 'inputMapping' | 'workflowInput' | 'unknown';

/** Per-token verdict, ranked most → least specific. */
export type TokenState =
  | { kind: 'covered' }
  | { kind: 'broken' }
  | { kind: 'upstream'; from: string[] }
  | { kind: 'missing' }
  | { kind: 'unknown' };

const tokenRe = /\{\{\s*([a-zA-Z_$][\w.$]*)\s*\}\}/g;

/** Unique top-level identifiers referenced by `{{tokens}}` in the prompt. */
export function extractTokens(instructions: string): string[] {
  const seen = new Set<string>();
  for (const m of instructions.matchAll(tokenRe)) {
    const name = m[1];
    if (name) seen.add(name);
  }
  return Array.from(seen);
}

export function parseJsonataRef(expr: string): { source: string; path: string | null } | null {
  const m = /^\s*\$\.(?:`([^`]+)`|([a-zA-Z_$][\w-]*))(?:\.(.+?))?\s*$/.exec(expr);
  if (!m) return null;
  const source = m[1] ?? m[2];
  if (!source) return null;
  return { source, path: m[3] ?? null };
}

/**
 * Inspect the schema BEHIND a mapped input key and return its
 * top-level field names so the UI can offer nested suggestions
 * (`{{device.id}}` etc.) without the author hand-writing them.
 *
 * Resolution mirrors `isMappingResolved`:
 *  - When `inputMapping[key]` is a JSONata ref like `$.preTriage.device`,
 *    walk to `preTriage`'s output schema → property `device` → its
 *    `properties` keys.
 *  - When `inputMapping[key]` is whole-output (`$.preTriage`), the
 *    children are the upstream schema's top-level keys.
 *  - When passthrough mode (no inputMapping, key comes straight from
 *    `workflowInputSchema.properties[key]`), walk into that property.
 *  - Anything else (literal, unresolvable ref, primitive-typed field):
 *    returns `[]` — caller renders a plain click-to-insert chip.
 */
export function getNestedFieldNamesForMappedKey(
  key: string,
  args: {
    inputMapping: Record<string, unknown>;
    workflowInputSchema: Record<string, unknown> | null;
    getOutputSchemaByNodeKey: (nodeKey: string) => Record<string, unknown> | null;
  },
): string[] {
  const raw = args.inputMapping[key];

  // Passthrough mode (no inputMapping entry): the key is itself a
  // workflow-input field. Walk one level into the trigger schema.
  if (raw === undefined) {
    if (!args.workflowInputSchema) return [];
    const props = (args.workflowInputSchema.properties ?? {}) as Record<string, unknown>;
    return getObjectFieldNames(props[key]);
  }

  // Literal value (anything not starting with `$`) — no static
  // nested fields we can describe at authoring time.
  if (typeof raw !== 'string' || !raw.startsWith('$')) return [];

  const ref = parseJsonataRef(raw);
  if (!ref) return [];

  const sourceSchema =
    ref.source === '__input' ? args.workflowInputSchema : args.getOutputSchemaByNodeKey(ref.source);
  if (!sourceSchema) return [];

  // Whole-output ref (`$.preTriage`): children are the schema's
  // top-level properties.
  if (ref.path === null) return getObjectFieldNames(sourceSchema);

  // Field ref (`$.preTriage.device[.subfield]`): walk to the named
  // top-level field's sub-schema. Deeper paths aren't expanded —
  // the dropdown is one level deep by design.
  const props = (sourceSchema.properties ?? {}) as Record<string, unknown>;
  const head = ref.path.split('.')[0];
  if (!head) return [];
  return getObjectFieldNames(props[head]);
}

function getObjectFieldNames(schema: unknown): string[] {
  if (!schema || typeof schema !== 'object') return [];
  const s = schema as { type?: unknown; properties?: unknown };
  // `type` may be omitted in practice (lots of authored schemas just
  // declare `properties` without explicitly saying `type: 'object'`).
  // Treat presence of an object-shaped `properties` map as the signal.
  if (!s.properties || typeof s.properties !== 'object') return [];
  return Object.keys(s.properties as Record<string, unknown>);
}

/**
 * Top-level keys exposed by the workflow trigger schema. Returned
 * INDEPENDENTLY of `inputMapping` so the UI can offer them as
 * suggestion chips even when the node has wired-up inputs — clicking
 * one adds `<key>: $.__input.<key>` to inputMapping and reads as a
 * mapped key from then on.
 */
export function getWorkflowInputKeys(schema: Record<string, unknown> | null): string[] {
  if (!schema || typeof schema !== 'object') return [];
  const props = (schema as { properties?: unknown }).properties;
  if (!props || typeof props !== 'object') return [];
  return Object.keys(props as Record<string, unknown>).sort();
}

/**
 * Top-level field names from a single key in the workflow trigger
 * schema — for the nested dropdown on a workflow-input chip BEFORE
 * the user has added it to inputMapping (so it doesn't yet show up
 * in `getNestedFieldNamesForMappedKey`).
 */
export function getWorkflowInputNestedFields(
  schema: Record<string, unknown> | null,
  key: string,
): string[] {
  if (!schema || typeof schema !== 'object') return [];
  const props = (schema as { properties?: unknown }).properties;
  if (!props || typeof props !== 'object') return [];
  return getObjectFieldNames((props as Record<string, unknown>)[key]);
}

export function isMappingResolved(
  value: unknown,
  ctx: {
    workflowInputSchema: Record<string, unknown> | null;
    getOutputFieldNamesByNodeKey: (nodeKey: string) => string[];
  },
): boolean | null {
  if (typeof value !== 'string' || !value.startsWith('$')) return true;
  const ref = parseJsonataRef(value);
  if (!ref) return null;
  const headOfPath = ref.path?.split('.')[0] ?? null;
  if (ref.source === '__input') {
    if (!ctx.workflowInputSchema) return null;
    const props = (ctx.workflowInputSchema.properties ?? {}) as Record<string, unknown>;
    if (!headOfPath) return Object.keys(props).length > 0;
    return headOfPath in props;
  }
  const fields = ctx.getOutputFieldNamesByNodeKey(ref.source);
  if (fields.length === 0) return null;
  if (!headOfPath) return true;
  return fields.includes(headOfPath);
}

export type KeyResolution = 'resolved' | 'broken' | 'unknown';

export function buildKeyResolution(args: {
  inputMapping: Record<string, unknown>;
  workflowInputSchema: Record<string, unknown> | null;
  getOutputFieldNamesByNodeKey: (nodeKey: string) => string[];
}): { keyResolution: Map<string, KeyResolution>; source: CoverageSource } {
  const mappingEntries = Object.entries(args.inputMapping);
  if (mappingEntries.length > 0) {
    const out = new Map<string, KeyResolution>();
    for (const [k, v] of mappingEntries) {
      const r = isMappingResolved(v, args);
      out.set(k, r === true ? 'resolved' : r === false ? 'broken' : 'unknown');
    }
    return { keyResolution: out, source: 'inputMapping' };
  }
  const wfProps = (args.workflowInputSchema?.properties ?? {}) as Record<string, unknown>;
  const wfKeys = Object.keys(wfProps);
  if (wfKeys.length > 0) {
    const out = new Map<string, KeyResolution>();
    for (const k of wfKeys) out.set(k, 'resolved');
    return { keyResolution: out, source: 'workflowInput' };
  }
  return { keyResolution: new Map(), source: 'unknown' };
}

/** Effective output field → upstream node keys that declare it. */
export function buildUpstreamAvailability(args: {
  incomingFromKeys: string[];
  getOutputFieldNamesByNodeKey: (nodeKey: string) => string[];
}): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const upKey of args.incomingFromKeys) {
    for (const propName of args.getOutputFieldNamesByNodeKey(upKey)) {
      const list = out.get(propName) ?? [];
      list.push(upKey);
      out.set(propName, list);
    }
  }
  return out;
}

export function classifyToken(args: {
  token: string;
  keyResolution: Map<string, KeyResolution>;
  source: CoverageSource;
  upstreamAvailability: Map<string, string[]>;
}): TokenState {
  const head = args.token.split('.')[0] ?? args.token;

  if (args.source !== 'unknown') {
    const resolution = args.keyResolution.get(args.token) ?? args.keyResolution.get(head);
    if (resolution === 'resolved') return { kind: 'covered' };
    if (resolution === 'broken') return { kind: 'broken' };
    if (resolution === 'unknown' && args.source === 'inputMapping') return { kind: 'unknown' };
  }

  const upstreamSources = args.upstreamAvailability.get(head);
  if (upstreamSources && upstreamSources.length > 0) {
    return { kind: 'upstream', from: upstreamSources };
  }

  if (args.source === 'unknown') return { kind: 'unknown' };
  return { kind: 'missing' };
}

export interface ITokenStateLabel {
  icon: string;
  tooltip: string;
}

export function describeTokenState(state: TokenState, token: string): ITokenStateLabel {
  const head = token.split('.')[0] ?? token;
  switch (state.kind) {
    case 'covered':
      return { icon: '✓', tooltip: `Will be filled in at runtime.` };
    case 'broken':
      return {
        icon: '⚠',
        tooltip: `This won’t fill in — the input mapping for "${head}" points to a field that doesn’t exist.`,
      };
    case 'upstream': {
      const nodes = state.from.map((s) => `"${s}"`).join(', ');
      return {
        icon: '↑',
        tooltip: `Available from ${state.from.length > 1 ? 'nodes' : 'node'} ${nodes} — add it under Inputs to use.`,
      };
    }
    case 'missing':
      return {
        icon: '⚠',
        tooltip: `Nothing provides this value — it’ll be empty at runtime.`,
      };
    case 'unknown':
    default:
      return {
        icon: '?',
        tooltip: `Can’t tell yet — set a workflow input schema or wire this node’s inputs.`,
      };
  }
}

export function captionForSource(source: CoverageSource): string {
  switch (source) {
    case 'inputMapping':
      return 'How each token will fill in based on this node’s wired inputs:';
    case 'workflowInput':
      return 'No wired inputs — workflow trigger fields pass through as-is:';
    case 'unknown':
    default:
      return 'No wired inputs yet. Tokens matching the workflow trigger or upstream nodes are highlighted below:';
  }
}
